<template>
  <div>
    <EquipmentHeader>
      <template #title>
        <div>Intake Gas Separation</div>
      </template>
    </EquipmentHeader>
    <div class="lg:flex align-items-center gap-4">
      <div class="flex align-items-center gap-2 flex-grow-1">
        <ConverterInput
          :class="{ 'custom-override': isOverridden }"
          :label="
            packerInstalled
              ? 'Intake Separation (Disabled)'
              : isOverridden
                ? 'Intake Separation (Override)'
                : 'Intake Separation (Calculated)'
          "
          mode="write"
          :disabled="
            packerInstalled || isScenarioReadonly || isImportedFromLegacy
          "
          :base-unit="PercentageUnits.Percentage"
          :model="PercentageUnits.options()"
          :model-value="currentSeparationValue"
          @update:model-value="handleValueChange"
        />
        <i
          v-if="isOverridden"
          v-tooltip.top="overrideTooltip"
          class="pi pi-eye override-flag-icon"
          aria-label="Override active"
        />
        <Button
          v-if="isOverridden && !isScenarioReadonly && !isImportedFromLegacy"
          v-tooltip.top="'Reset to Calculated Value'"
          icon="pi pi-undo"
          class="p-button-text p-button-rounded"
          :disabled="packerInstalled"
          @click="resetToCalculated"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { FormItems } from "@/composables/forms/formTypes";

import { computed } from "vue";

import EquipmentHeader from "./EquipmentHeader.vue";
import ConverterInput from "../ConverterInput.vue";
import Button from "primevue/button";
import {
  parametersKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { convert } from "@/libs/units";
import { PercentageUnits } from "@/libs/units/percentage";
import { injectStrict } from "@/utils/utils";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import { getFieldValue } from "@/composables/forms/stackAwareFields";
import { useGasSeparationOverride } from "@/composables/forms/useGasSeparationOverride";
import { usePackerWatcher } from "@/composables/forms/usePackerWatcher";
import type { StackLocation } from "@/components/equipment/d";

const parameters = injectStrict(parametersKey);
const { intakeCalculations } = injectStrict(scenarioCalculationsKey);
const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();

const props = withDefaults(
  defineProps<{
    form: FormItems["parameters"] | FormItems["performanceEvaluation"];
    updateScenario?: boolean;
    stack?: StackLocation;
  }>(),
  {
    updateScenario: true,
    stack: undefined
  }
);

const percentCalcIntakeGasSep = computed(() => {
  if (props.stack === "lower") {
    return 0;
  }
  return convert(
    intakeCalculations.aggregate.calculatedIntakeGasSeparationEfficiencyRatio
      .value || 0,
    PercentageUnits.Ratio,
    PercentageUnits.Percentage
  );
});

// Extract packer installed check - type-safe
const packerInstalled = computed(
  () =>
    (getFieldValue(parameters.value, "packerInstalled", props.stack) as
      | boolean
      | undefined) ?? false
);

// Use composable for gas separation override
const {
  isOverridden,
  currentValue: currentSeparationValue,
  overrideTooltip,
  handleValueChange,
  resetToCalculated
} = useGasSeparationOverride(
  props.form,
  "intakeGasSepOverrideType",
  "intakeGasSepPercent",
  percentCalcIntakeGasSep,
  packerInstalled,
  props.stack
);

// Use composable for packer watcher (handles storing/restoring override state)
usePackerWatcher(
  props.form,
  packerInstalled,
  {
    intakeGasSeparation: {
      overrideTypeFieldKey: "intakeGasSepOverrideType",
      percentFieldKey: "intakeGasSepPercent",
      calculatedValue: percentCalcIntakeGasSep
    }
  },
  props.stack
);
</script>

<style scoped>
.custom-override {
  position: relative;
  border: 2px solid var(--info-500) !important;
  border-radius: 4px;
  padding: 0.25rem;
  box-shadow: 0 0 0.5rem rgba(33, 150, 243, 0.4) !important;
  background-color: rgba(33, 150, 243, 0.05);
}

.override-flag-icon {
  color: var(--info-500);
  font-size: 1.1rem;
  cursor: help;
}
</style>
